import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

export const ACL_DEVTOOLS_ENABLED =
  import.meta.env.DEV || import.meta.env.VITE_ACL_DEVTOOLS === 'true';

export interface AclCheck {
  resource: string;
  action: string;
  result: boolean;
}

export interface AclCheckEntry {
  id: string;
  /** One or more resource:action checks AND'd together to gate this element */
  checks: AclCheck[];
  /** Overall pass/fail — false if any check in `checks` fails */
  result: boolean;
  wrapperRef: React.RefObject<HTMLElement | null>;
}

interface AclDevToolsContextValue {
  register: (entry: AclCheckEntry) => () => void;
  entries: AclCheckEntry[];
  isOpen: boolean;
  toggleOpen: () => void;
  highlightAll: boolean;
  setHighlightAll: (v: boolean) => void;
}

export const AclDevToolsContext = createContext<AclDevToolsContextValue | null>(null);

export function AclDevToolsProvider({ children }: { children: React.ReactNode }) {
  if (!ACL_DEVTOOLS_ENABLED) return <>{children}</>;
  return <AclDevToolsProviderInner>{children}</AclDevToolsProviderInner>;
}

function AclDevToolsProviderInner({ children }: { children: React.ReactNode }) {
  const [entries, setEntries] = useState<AclCheckEntry[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightAll, setHighlightAll] = useState(false);

  const register = useCallback((entry: AclCheckEntry) => {
    setEntries(prev => (prev.some(e => e.id === entry.id) ? prev : [...prev, entry]));
    return () => setEntries(prev => prev.filter(e => e.id !== entry.id));
  }, []);

  const toggleOpen = useCallback(() => setIsOpen(v => !v), []);

  const value = useMemo(
    () => ({ register, entries, isOpen, toggleOpen, highlightAll, setHighlightAll }),
    [register, entries, isOpen, toggleOpen, highlightAll, setHighlightAll],
  );

  return (
    <AclDevToolsContext.Provider value={value}>
      {children}
    </AclDevToolsContext.Provider>
  );
}

export const useAclDevToolsCtx = () => useContext(AclDevToolsContext);
